import type { ICrossCollateralBlanket } from 'pages/LoanStructure/interfaces'

export interface ILtvValues {
  aivLtv: number
  arvLtv: number
  ltc: number
  ltp: number
}

export interface ILoanPurposeLTVValues {
  purchase: ILtvValues
  nocashout: ILtvValues
  cashout: ILtvValues
}

export interface IRatePriceAdjustmentValue {
  rate: number
  price: number
}

export interface IRange {
  from: number
  to: number
}

export interface ILoanPurposeValues {
  purchase: number
  nocashout: number
  cashout: number
}

export interface ILabelValue {
  label: string
  value: string
}

export interface IRatePriceAdjustment {
  purchase: IRatePriceAdjustmentValue[]
  nocashout: IRatePriceAdjustmentValue[]
  cashout: IRatePriceAdjustmentValue[]
}

export interface IMaxCashToBorrowerLeverageLimit {
  ltv: IRange
  citizenship: string[]
  limit: {
    FICO: IRange
    loanAmount: IMinMaxLimit
    minDSCR: number
    maxValue: number
    maxLoanAmount: number
  }[]
}

export interface INumberOfPropertiesLimit {
  min: number
  max: number
}

export interface IMinMaxLimit {
  min: number
  max: number
}

export interface INonDscrLTVs {
  aivLtv: number
  arvLtv: number
  ltc: number
  ltp: number
  cltv: number
}

export interface ISecondLienLimit {
  maxCLTV: number
  maxSecondLien: number
}

// Non-DSCR leverage limit interfaces
export interface IFICORangeLimits {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: ILoanPurposeLTVValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IExperienceLtvMaxLimits {
  from: number
  to: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: ILoanPurposeLTVValues
    minMonthsReserve: ILoanPurposeValues
    maxConstructionBudget: ILoanPurposeValues
  }[]
}

export interface IPropertyTypeLeverageData {
  value: string
  numberOfUnits: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  minExperience: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: {
      purchase: INonDscrLTVs
      nocashout: INonDscrLTVs
      cashout: INonDscrLTVs
    }
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface ICitizenshipLtvMaxLimits {
  value: string
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: {
    FICO: IRange
    LTV: ILoanPurposeLTVValues
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    minExperience: number
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface IBankruptcyRange {
  chapter: number
  from: number
  to: number
}

export interface IMortgageLatesRange {
  from: number
  to: number
  days: number
  months: number
}

export interface IValueLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IFirstTimeInvestorLeverageLimit {
  value: string
  propertyTypes: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IBankruptcyLeverageLimit {
  value: IBankruptcyRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IMortgageLatesLeverageLimit {
  value: IMortgageLatesRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IRangeLeverageLimit {
  value: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IRuralPropertyLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: {
    FICO: IRange
    propertyTypes: string[]
    LTV: ILoanPurposeLTVValues
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface IRehabBudgetTypeLimit {
  expRange: IRange
  loanAmount: IMinMaxLimit
  limits: IFICORangeLimits[]
}

export interface IRehabBudgetTypeLtvMaxLimits {
  value: string
  limits: IRehabBudgetTypeLimit[]
}

export interface IFicoLeverageLimit {
  ficoRange: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: ILoanPurposeLTVValues
  minMonthsReserve: ILoanPurposeValues
  minExperience: ILoanPurposeValues
}

export interface ILoanAmountLeverageLimit {
  loanAmount: IRange
  LTV: ILoanPurposeLTVValues
}

export interface IOtherLimit {
  minAsIsAppraisedValue: ILoanPurposeValues
  minAfterRepairValue: ILoanPurposeValues
  minAIVPricePerDoor: ILoanPurposeValues
  minARVPricePerDoor: ILoanPurposeValues
}

export interface ILeverageMaxLimits {
  experienceLtvLimits: IExperienceLtvMaxLimits[]
  propertyTypeLtvLimits: IPropertyTypeLeverageData[]
  occupancyLeverageLimit: IValueLeverageLimit[]
  decliningMarketPropertyLeverageLimit: IValueLeverageLimit[]
  newConstructionPropertyLeverageLimit: IValueLeverageLimit[]
  firstTimeHomeBuyerLeverageLimit: IValueLeverageLimit[]
  firstTimeInvestorLeverageMaxLimit: IFirstTimeInvestorLeverageLimit[]
  bankruptcyLeverageMaxLimit: IBankruptcyLeverageLimit[]
  mortgageLatesLtvLimits: IMortgageLatesLeverageLimit[]
  fcSsDilLeverageMaxLimit: IRangeLeverageLimit[]
  forbearanceLeverageLimit: IRangeLeverageLimit[]
  citizenshipLtvLimits: ICitizenshipLtvMaxLimits[]
  ruralPropertyLeverageMaxLimit: IRuralPropertyLeverageLimit[]
  rehabBudgetTypeLtvLimits: IRehabBudgetTypeLtvMaxLimits[]
  ficoLeverageLimit: IFicoLeverageLimit[]
  loanAmountLeverageLimit: ILoanAmountLeverageLimit[]
  maxCashToBorrowerLeverageMaxLimit: IMaxCashToBorrowerLeverageLimit[]
  crossCollateralBlanket: ICrossCollateralBlanket
  secondLienMaxLimit: ISecondLienLimit
  otherLimit: IOtherLimit
}

// DSCR leverage limit interfeaces
export interface IDscrLTVs {
  ltv: IMinMaxLimit
  cltv: number
}

export interface IDscrFicoLeverageLimit {
  ficoRange: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  minDSCR: ILoanPurposeValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IDSCRFICORangeLimits {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  minDSCR: ILoanPurposeValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IDSCRPropertyTypeLimits {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  DSCR: { purchase: IRange; nocashout: IRange; cashout: IRange }
  minMonthsReserve: ILoanPurposeValues
}

export interface IDscrPropertyTypeLeverageLimit {
  value: string
  numberOfUnits: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRPropertyTypeLimits[]
}

export interface IDscrValueLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrOccupancyLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: {
      purchase: IDscrLTVs
      nocashout: IDscrLTVs
      cashout: IDscrLTVs
    }
    minDSCR: ILoanPurposeValues
    minMonthsReserve: ILoanPurposeValues
    minPPP: ILoanPurposeValues
  }[]
}

export interface IDscrFirstTimeInvestorLeverageLimit {
  value: string
  propertyTypes: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrBankruptcyLeverageLimit {
  value: IBankruptcyRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrMortgageLatesLeverageLimit {
  value: IMortgageLatesRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrCustomRangeLeverageLimit {
  value: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrShortTermRentalLeverageLimit {
  value: string
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrRuralPropertyLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: {
      purchase: IDscrLTVs
      nocashout: IDscrLTVs
      cashout: IDscrLTVs
    }
    minDSCR: ILoanPurposeValues
    minMonthsReserve: ILoanPurposeValues
    propertyTypes: string[]
  }[]
}

export interface IInterestOnlyLimit {
  citizenship: string[]
  limit: {
    fico: IRange
    loanAmount: IRange
    DSCR: IRange
    minMonthsReserve: number
    LTV: IMinMaxLimit
    maxCLTV: number
  }[]
}

export interface IDscrInterestOnlyLeverageLimit {
  purchase: IInterestOnlyLimit
  nocashout: IInterestOnlyLimit
  cashout: IInterestOnlyLimit
}

export interface IDscrRangeLeverageLimit {
  dscr: IRange
  propertyTypes: string[]
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  limit: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  minFICO: ILoanPurposeValues
}

export interface IDscrLoanAmountLeverageLimit {
  loanAmount: IRange
  LTV: ILoanPurposeValues
  CLTV: ILoanPurposeValues
}

export interface IDscrLTVLeverageLimit {
  value: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    CLTV: ILoanPurposeValues
    minDSCR: ILoanPurposeValues
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface IDscrLeverageMaxLimits {
  PropertyTypeLtvMaxLimit: IDscrPropertyTypeLeverageLimit[]
  OccupancyLeverageLimit: IDscrOccupancyLeverageLimit[]
  EscrowTypeLeverageLimit: IDscrValueLeverageLimit[]
  CitizenshipLtvMaxLimit: IDscrValueLeverageLimit[]
  DecliningMarketPropertyLeverageLimit: IDscrValueLeverageLimit[]
  NewConstructionPropertyLeverageLimit: IDscrValueLeverageLimit[]
  FirstTimeHomeBuyerLeverageLimit: IDscrValueLeverageLimit[]
  FirstTimeInvestorLeverageMaxLimit: IDscrFirstTimeInvestorLeverageLimit[]
  BankruptcyLeverageMaxLimit: IDscrBankruptcyLeverageLimit[]
  MortgageLatesLtvMaxLimit: IDscrMortgageLatesLeverageLimit[]
  FcSsDilLeverageMaxLimit: IDscrCustomRangeLeverageLimit[]
  ForbearanceLeverageLimit: IDscrCustomRangeLeverageLimit[]
  IsShortTermRentalLeverageLimit: IDscrShortTermRentalLeverageLimit[]
  RuralPropertyLeverageMaxLimit: IDscrRuralPropertyLeverageLimit[]
  InterestOnlyLeverageLimit: IDscrInterestOnlyLeverageLimit
  FICOLeverageLimit: IDscrFicoLeverageLimit[]
  DscrLeverageLimit: IDscrRangeLeverageLimit[]
  LoanAmountLeverageLimit: IDscrLoanAmountLeverageLimit[]
  LTVLeverageLimit: IDscrLTVLeverageLimit[]
  MaxCashToBorrowerLeverageMaxLimit: IMaxCashToBorrowerLeverageLimit[]
  CrossCollateralBlanket: ICrossCollateralBlanket
  SecondLienMaxLimit: ISecondLienLimit
}

export interface IPropertyTypeLeverageAdjustment {
  value: string
  numberOfUnits: IRange
  limit: ILoanPurposeLTVValues
}

export interface IValueLeverageData {
  value: string
  limit: ILoanPurposeLTVValues
}

export interface IAdvancedLeverageAdjustment {
  value: string
  adjustment: {
    loanAmount: IRange
    LTV: ILoanPurposeLTVValues
  }[]
}

export interface IBankruptcyLeverageAdjustment {
  value: IBankruptcyRange
  limit: ILoanPurposeLTVValues
}

export interface IMortgageLatesLeverageAdjustment {
  value: IMortgageLatesRange
  limit: ILoanPurposeLTVValues
}

export interface IRangeLeverageAdjustment {
  range: IRange
  limit: ILoanPurposeLTVValues
}

export interface IFicoLeverageAdjustment {
  range: IRange
  limit: ILoanPurposeLTVValues
  propertyTypes: string[]
}

export interface ILeverageAdjustments {
  maxLTVAdjustment: ILtvValues
  propertyTypeLeverageAdjustment: IPropertyTypeLeverageAdjustment[]
  occupancyLeverageAdjustment: IValueLeverageData[]
  underConstructionLeverageAdjustment: IValueLeverageData[]
  isPropertyInLeasableStateLeverageAdjustment: IValueLeverageData[]
  newConstructionPropertyLeverageAdjustment: IValueLeverageData[]
  citizenshipLeverageAdjustment: IValueLeverageData[]
  rehabBudgetTypeLeverageAdjustment: IValueLeverageData[]
  ruralPropertyLeverageAdjustment: IValueLeverageData[]
  firstTimeHomeBuyerLeverageAdjustment: IValueLeverageData[]
  firstTimeInvestorLeverageAdjustment: IValueLeverageData[]
  interestOnlyLeverageAdjustment: IValueLeverageData[]
  decliningMarketPropertyLeverageAdjustment: IAdvancedLeverageAdjustment[]
  ficoLeverageAdjustment: IFicoLeverageAdjustment[]
  bankruptcyLeverageAdjustment: IBankruptcyLeverageAdjustment[]
  mortgageLatesLeverageAdjustment: IMortgageLatesLeverageAdjustment[]
  fcSsDilLeverageAdjustment: IRangeLeverageAdjustment[]
  forbearanceLeverageAdjustment: IRangeLeverageAdjustment[]
  exitFeeLeverageAdjustment: IRangeLeverageAdjustment[]
  loanAmountLeverageAdjustment: IRangeLeverageAdjustment[]
  monthsReserveLeverageAdjustment: IRangeLeverageAdjustment[]
  experienceLeverageAdjustment: IRangeLeverageAdjustment[]
  loanPurposeLeverageAdjustment: ILoanPurposeLTVValues
}

export interface IDscrPropertyTypeLeverageData {
  value: string
  numberOfUnits: IRange
  limit: ILoanPurposeValues
}

export interface IDscrValueLeverageData {
  value: string
  limit: ILoanPurposeValues
}

export interface IDSCRAdvancedLeverageAdjustment {
  value: string
  adjustment: {
    loanAmount: IRange
    LTV: ILoanPurposeValues
  }[]
}

export interface IDscrBankruptcyLeverageAdjustment {
  value: IBankruptcyRange
  limit: ILoanPurposeValues
}

export interface IDscrMortgageLatesLeverageAdjustment {
  value: IMortgageLatesRange
  limit: ILoanPurposeValues
}

export interface IDscrRangeLeverageAdjustment {
  range: IRange
  limit: ILoanPurposeValues
}

export interface IDscrLeverageAdjustments {
  MaxLTVAdjustment: { LTV: number; CLTV: number }
  PropertyTypeLeverageAdjustment: IDscrPropertyTypeLeverageData[]
  LoanAmountLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  FcSsDilLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  ForbearanceLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  BankruptcyLeverageAdjustment: IDscrBankruptcyLeverageAdjustment[]
  MortgageLatesLeverageAdjustment: IDscrMortgageLatesLeverageAdjustment[]
  OccupancyLeverageAdjustment: IDscrValueLeverageData[]
  CitizenshipLeverageAdjustment: IDscrValueLeverageData[]
  IsPropertyInLeasableStateLeverageAdjustment: IDscrValueLeverageData[]
  IsShortTermRentalLeverageAdjustment: IDscrValueLeverageData[]
  NewConstructionPropertyLeverageAdjustment: IDscrValueLeverageData[]
  FirstTimeHomeBuyerLeverageAdjustment: IDscrValueLeverageData[]
  FirstTimeHomeInvestorLeverageAdjustment: IDscrValueLeverageData[]
  InterestOnlyLeverageAdjustment: IDscrValueLeverageData[]
  DecliningMarketPropertyLeverageAdjustment: IDSCRAdvancedLeverageAdjustment[]
}

export interface ICommercialOnlyProgrammed {
  leverageMaxLimits?: ILeverageMaxLimits
  leverageAdjustments?: ILeverageAdjustments
  baseRates: number[]
  basePrices: number[]
  rateSpreads: number[]
  exitFees: number[]
  ltvLimits: ILoanPurposeLTVValues
  maxRatePriceAdjustment: {
    purchase: IRatePriceAdjustmentValue
    nocashout: IRatePriceAdjustmentValue
    cashout: IRatePriceAdjustmentValue
  }
  loanPurposeAdjustment: {
    purchase: IRatePriceAdjustmentValue
    nocashout: IRatePriceAdjustmentValue
    cashout: IRatePriceAdjustmentValue
  }
  loanAmountRanges: IRange[]
  loanAmountAdjustment: IRatePriceAdjustment
  experience: IRange[]
  experienceAdjustment: IRatePriceAdjustment
  aivLtvs: IRange[]
  aivLtvAdjustment: IRatePriceAdjustment
  ltcs: IRange[]
  ltcAdjustment: IRatePriceAdjustment
  ficos: IRange[]
  ficoAdjustment: IRatePriceAdjustment
  monthsReserve: IRange[]
  monthsReserveAdjustment: IRatePriceAdjustment
  loanTerm: IRange[]
  loanTermAdjustment: IRatePriceAdjustment
  foreclosure: IRange[]
  foreclosureAdjustment: IRatePriceAdjustment
  deedInLieu: IRange[]
  deedInLieuAdjustment: IRatePriceAdjustment
  shortsale: IRange[]
  shortsaleAdjustment: IRatePriceAdjustment
  forbearance: IRange[]
  forbearanceAdjustment: IRatePriceAdjustment
  propertyTypes: {
    value: string
    numberOfUnits: IRange
  }[]
  propertyTypeAdjustment: IRatePriceAdjustment
  state: string[]
  stateAdjustment: IRatePriceAdjustment
  bankruptcy: IBankruptcyRange[]
  bankruptcyAdjustment: IRatePriceAdjustment
  mortgageLates: IMortgageLatesRange[]
  mortgageLatesAdjustment: IRatePriceAdjustment
  citizenships: ILabelValue[]
  citizenshipAdjustment: IRatePriceAdjustment
  rehabBudgetTypes: ILabelValue[]
  rehabBudgetTypesAdjustment: IRatePriceAdjustment
  loanAmount: IRange
  initialLoanAmount: IRange
}

export interface IProgramTableData {
  ID: number
  Name: string
  Type: string
  tableData: any[]
  tableStyle: Record<string, any>
  leverageMaxLimits?: ILeverageMaxLimits
  leverageAdjustments?: ILeverageAdjustments
  StaticTable: IStaticTable[]
}

export interface ILtvValues {
  aivLtv: number
  arvLtv: number
  ltc: number
  ltp: number
}

export interface IPropertyTypeLtvAdj {
  propertyType: string
  unitsRange: IRange
  aivValue: IRange
  arvValue: IRange
  ltvLimit: ILoanPurposeLTVValues
  ltvAdjustment: ILoanPurposeLTVValues
}

export interface IExperienceTierLimit {
  expRange: IRange
  limit: {
    FICO: IRange
    maxLoanAmount: ILoanPurposeValues
    ltvLimit: ILoanPurposeLTVValues
    ltvAdjustment: ILoanPurposeLTVValues
  }[]
}

export interface IExperienceTierData {
  title: string
  count: number
  limit: IExperienceTierLimit[]
}

export interface ITier {
  title: string
  count: number
  propertyTypes?: string[]
  ltvLimit?: ILoanPurposeLTVValues
  ltvAdjustment?: ILoanPurposeLTVValues
  propertyTypeLtvAdj?: IPropertyTypeLtvAdj[]
}

export interface IExperienceTier {
  [key: string]: IExperienceTierData[]
}

export interface ITierData {
  [key: string]: ITier[] | IExperienceTier
}

export interface IStaticTable {
  title: string
  values: string[]
}

export interface IBridgeRate {
  FICO: IRange
  interestRates: number[]
}

export interface IBridgeRatePrice {
  rate: IBridgeRate[]
  price: number
  exitFee: number
  rateSpread: number
  incremental: {
    rate: number
    price: number
    exitFee: number
    rateSpread: number
  }
  maximum: {
    rate: number
    price: number
    exitFee: number
    rateSpread: number
  }
}

export enum NonDSCRTemplateType {
  NonLeverageBased,
  LeverageBased,
}

export interface IProgramData {
  ID: number
  Name: string
  Type: string
  ProductType: string
  TemplateType: NonDSCRTemplateType
  Restriction: IDscrRestriction
  ProgramData: ICommercialOnlyProgrammed
  LTV: IRange[]
  BridgeData: IBridgeRatePrice
  TierData: ITierData
  StaticTable: IStaticTable[]
}

export interface ILoanAmountDscrOverlay {
  loanAmount: IRange
  minDscr: number
}

export interface ILoanPurposeDscrOverlay {
  loanPurpose: string
  minDscr: number
}

export interface IDscrOverlay {
  loanAmountDscrOverlay?: ILoanAmountDscrOverlay[]
  loanPurposeDscrOverlay?: ILoanPurposeDscrOverlay[]
}

export interface ILTVData {
  title: string
  DSCR?: IRange
  data: ILoanAmountFICOMaxLTV[]
}

export interface IMatrixLTVData {
  label: string
  key: string
  occupancy: string
  maxLTVs: ILTVData[]
}

export interface IDscrRestriction {
  MatrixLTVData?: IMatrixLTVData[]
  PPPOverlay?: IPrepaymentPenaltyOverlay[]
  LeverageMaxLimits?: IDscrLeverageMaxLimits
  LeverageAdjustments?: IDscrLeverageAdjustments
  DscrOverlay?: IDscrOverlay
  DscrOccupancyLtvReduction?: number
  MinRate?: number
  RateReduction?: number
}

export interface ILoanAmountFICOMaxLTV {
  loanAmount: IRange
  ficos: number[]
  monthsReserve: number
  purchaseRT: number[]
  nocashout: number[]
  cashout: number[]
}

export interface Ltv {
  title: string
  data: ILoanAmountFICOMaxLTV[]
}

export interface StateLTVMaxLimit {
  label: string
  limits: Record<string, number>
}

export enum DocumentType {
  GUIDELINE = 'guideline',
  FORM = 'form',
  TRAINING = 'training',
  RESISDENTIAL = 'resisdential',
  MARKETING = 'marketing',
}

export interface IDocument {
  id: number
  name: string
  orderId: number
  fileKey: string
  type: DocumentType
  programIDs: string[]
  createdAt: Date
  updatedAt: Date
}

export interface IPrepaymentPenaltyOverlay {
  label: string
  minPPP: number
}
